<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { AisRefinementList } from 'vue-instantsearch/vue3/es';
import { ref } from 'vue';
import { AkTag } from '@ankorstore/design-system';
import { useBreakpoints } from '@monolith/legacy/modules/design-system-candidates';

const props = defineProps<{
  attribute: string;
  nameById?: Record<string, string>;
}>();

const { t } = useI18n();

const resolveLabel = (id: string | number): string => props.nameById?.[String(id)] ?? String(id);

type RefinementItem = {
  label: string;
  value: string;
  count: number;
  isRefined: boolean;
  highlighted: string;
};

const SORT_BY = ['count:desc', 'name:asc'] as const;
const MAX_VISIBLE = 4;

const { isAboveOrEqualLg } = useBreakpoints();
const isExpanded = ref(false);

const validItems = (items: RefinementItem[]): RefinementItem[] =>
  items.filter((item) => item.label.trim() !== '');

const totalCount = (items: RefinementItem[]): number =>
  items.reduce((sum, item) => sum + item.count, 0);

const hasActiveRefinement = (items: RefinementItem[]): boolean =>
  items.some((item) => item.isRefined);

const handleAllProductsClick = (
  items: RefinementItem[],
  refine: (value: string) => void
) => {
  items.filter((item) => item.isRefined).forEach((item) => refine(item.value));
};
</script>

<template>
  <AisRefinementList
    :attribute="props.attribute"
    :limit="100"
    :sort-by="SORT_BY"
  >
    <template #default="{ items, refine }">
      <!-- v-for trick to bind validItems(items) once per render instead of calling it 6 times -->
      <template
        v-for="[vi] in [[validItems(items)]]"
        :key="vi.map(i => `${i.value}:${i.isRefined}`).join('|')"
      >
        <div
          v-if="vi.length > 0"
          class="product-type-shortcuts__list ds-flex ds-flex-wrap ds-gap-2 ds-items-center"
        >
          <AkTag
            color="neutral"
            :outlined="hasActiveRefinement(vi)"
            :class="{ 'product-type-shortcuts__tag--active': !hasActiveRefinement(vi) }"
            size="sm"
            :label="t('discovery.catalog.productTypeShortcuts.allProducts', { count: totalCount(vi) })"
            @click="handleAllProductsClick(vi, refine)"
          />

          <AkTag
            v-show="!isAboveOrEqualLg || isExpanded || index < MAX_VISIBLE"
            v-for="(item, index) in vi"
            :key="item.value"
            color="neutral"
            :outlined="!item.isRefined"
            :class="{ 'product-type-shortcuts__tag--active': item.isRefined }"
            size="sm"
            :label="`${resolveLabel(item.value)} (${item.count})`"
            @click="refine(item.value)"
          />

          <AkTag
            v-if="isAboveOrEqualLg && !isExpanded && vi.length > MAX_VISIBLE"
            color="neutral"
            outlined
            size="sm"
            :label="t('discovery.catalog.productTypeShortcuts.seeMore', { count: vi.length - MAX_VISIBLE })"
            @click="isExpanded = true"
          />

          <AkTag
            v-if="isAboveOrEqualLg && isExpanded && vi.length > MAX_VISIBLE"
            color="neutral"
            outlined
            size="sm"
            :label="t('discovery.catalog.productTypeShortcuts.seeLess')"
            @click="isExpanded = false"
          />
        </div>
      </template>
    </template>
  </AisRefinementList>
</template>

<style scoped lang="scss">
.product-type-shortcuts {
  &__list {
    @media (max-width: 1023px) {
      @apply ds-flex-nowrap ds-overflow-x-auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        @apply ds-hidden;
      }

      :deep(*) {
        @apply ds-flex-shrink-0;
      }
    }
  }

  &__tag--active {
    @apply ds-bg-success-700 #{!important};

    :deep(.ak-tag__button) {
      @apply ds-text-accent-alt ds-font-semibold;
    }
  }
}
</style>
